import type { DeparturePort, Platform } from '@sirius/contracts';

/**
 * Підписи марок, моделей і портів.
 *
 * Провайдер віддає все капсом: «2016 CADILLAC ESCALADE LUXURY». Читати капс у
 * сітці з двадцяти чотирьох карток важко, але скорочення лишаються
 * скороченнями — «Bmw» чи «Rav4» виглядали б помилкою.
 *
 * Файл імпортує лише типи: його функції тестуються вбудованим node:test без
 * збірки (див. features/catalogue/make-model.test.ts).
 */

const titleCase = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

/** Марки, які пишуться капсом, хоч і довші за дві літери. */
const MAKE_ACRONYMS = new Set(['BMW', 'GMC', 'RAM', 'MINI', 'FIAT', 'KIA', 'MG', 'BYD']);

/** Марки з великою літерою всередині слова: правило «перша велика» дало б «Mclaren». */
const MAKE_SPELLING: Record<string, string> = { MCLAREN: 'McLaren' };

export function makeLabel(raw: string): string {
	return raw
		.split(/([\s-]+)/)
		.map((part) => MAKE_SPELLING[part.toUpperCase()] ?? (MAKE_ACRONYMS.has(part.toUpperCase()) ? part.toUpperCase() : titleCase(part)))
		.join('');
}

/** Позначення комплектацій і кузовів, які мають голосні, але лишаються капсом. */
const TRIM_CODES = new Set(['AWD', 'FWD', 'RWD', 'XLE', 'SUV', 'AMG', 'GTI', 'GLE', 'GLA', 'CLA', 'ILX', 'EXL']);

/** Код, а не слово: з цифрою, з однієї-двох літер, без голосних (MKZ, MFG, XLT) або відоме позначення. */
function isCode(part: string): boolean {
	const p = part.trim().toUpperCase();
	return /\d/.test(p) || p.length <= 2 || !/[AEIOUY]/.test(p) || TRIM_CODES.has(p);
}

/**
 * Привід BMW виробник пише з малої першої літери: «xDrive40e», «eDrive»,
 * «sDrive30i». Провайдер віддає «XDRIVE40E» — капсом це читається як номер
 * деталі. Суфікс після цифр (i, e, d) теж малий.
 */
function bmwDrive(part: string): string | null {
	const m = /^([XSE])DRIVE(\d*)([A-Z]*)$/.exec(part.trim().toUpperCase());
	if (!m) return null;
	const [, kind = '', digits = '', suffix = ''] = m;
	return `${kind.toLowerCase()}Drive${digits}${suffix.toLowerCase()}`;
}

/**
 * Моделі: слова — з великої, коди лишаються як є: «RAV4», «CR-V», «F-150»,
 * «X5», «MKZ», «AWD»; привід BMW — як у виробника.
 */
export function modelLabel(raw: string): string {
	return raw
		.split(/([\s-]+)/)
		.map((part) => {
			if (/^[\s-]*$/.test(part)) return part;
			return bmwDrive(part) ?? (isCode(part) ? part.toUpperCase() : titleCase(part));
		})
		.join('');
}

/**
 * Назва лота, збережена в наших таблицях (ставка, авто, інвойс), прийшла від
 * провайдера капсом: «2013 LINCOLN MKZ HYBRID». Кабінет показує її тими
 * самими правилами, що й каталог, — інакше на одному екрані стоять дві
 * різні назви того самого авто.
 */
export const storedLotTitle = (raw: string): string => modelLabel(raw);

/** «2016 Cadillac Escalade Luxury» — назва лота для людей, а не для бази даних. */
export function lotTitle(v: {
	year: number | null;
	make: string | null;
	model: string | null;
	modelFull?: string | null;
}): string {
	const model = v.modelFull ?? v.model;
	const parts = [v.year === null ? null : String(v.year), v.make && makeLabel(v.make), model && modelLabel(model)].filter(
		(p): p is string => Boolean(p),
	);
	return parts.length > 0 ? parts.join(' ') : 'Unidentified lot';
}

/**
 * Назва лота двома частинами для заголовка сторінки: «2026 GMC Terrain» і
 * комплектація «AWD Denali» окремо — друга набирається світлішим, бо модель
 * дилер впізнає першою, а комплектацію дочитує.
 *
 * Якщо повна модель не починається з короткої, розділити чесно не вийде —
 * тоді вся назва йде першою частиною.
 */
export function lotTitleParts(v: {
	year: number | null;
	make: string | null;
	model: string | null;
	modelFull?: string | null;
}): { main: string; trim: string | null } {
	const model = v.model?.trim() ?? '';
	const full = v.modelFull?.trim() ?? '';
	const rest = model && full.toUpperCase().startsWith(`${model.toUpperCase()} `) ? full.slice(model.length).trim() : '';
	if (!rest) return { main: lotTitle(v), trim: null };
	return { main: lotTitle({ ...v, modelFull: null }), trim: modelLabel(rest) };
}

/** «Copart», «IAAI» — для речень; невідомий майданчик — просто «the auction». */
export function platformLabel(platform: Platform | null): string {
	if (platform === 'copart') return 'Copart';
	if (platform === 'iaai') return 'IAAI';
	return 'the auction';
}

/** Код LA — як у провайдера; клієнт возить через Long Beach (рішення 2026-10-22). */
const PORT_LABELS: Record<DeparturePort, string> = {
	NY: 'New York',
	Savannah: 'Savannah',
	Houston: 'Houston',
	LA: 'Long Beach',
};

export function portLabel(port: DeparturePort): string {
	return PORT_LABELS[port];
}

/** «Orlando (FL)» → «Orlando, FL»: так місто читається як адреса, а не як примітка. */
export function yardLabel(display: string): string {
	return display.replace(/\s*\(([A-Z]{2})\)\s*$/, ', $1');
}
